/**
 * Minimal cookie read / write / delete helpers scoped to `document.cookie`.
 * Dedicated to the dev-area session today — other features use
 * HttpOnly cookies set server-side, which are inaccessible from JS.
 *
 * Limitations to know going in:
 *   - JS-set cookies cannot be `HttpOnly`. Anything written here is
 *     readable by any script running on the same origin. Balance
 *     that against the alternative (localStorage / sessionStorage)
 *     per feature. For the dev area, the JWT is already returned in
 *     a response body (not HttpOnly), so cookie storage doesn't
 *     worsen the XSS surface.
 *   - `Secure` is only added when the page is served over HTTPS.
 *     Plain-HTTP `localhost` dev still works.
 *   - `SameSite=Strict` is the default — JS-reads are never blocked
 *     by SameSite, it only governs cross-site cookie sends, which
 *     we don't do. Strict is the tightest safe setting.
 */

export interface SetCookieOptions {
  /** Seconds until the cookie expires. Omit to create a session cookie. */
  maxAgeSeconds?: number;
  /** Path scope. Defaults to `/`. */
  path?: string;
  /** SameSite policy. Defaults to `Strict`. */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const needle = name + '=';
  for (const entry of document.cookie.split('; ')) {
    if (entry.startsWith(needle)) {
      const raw = entry.substring(needle.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
}

export function setCookie(
  name: string,
  value: string,
  options: SetCookieOptions = {}
): void {
  if (typeof document === 'undefined') {
    return;
  }
  const path = options.path ?? '/';
  const sameSite = options.sameSite ?? 'Strict';
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  const maxAge =
    typeof options.maxAgeSeconds === 'number' && options.maxAgeSeconds > 0
      ? `; Max-Age=${Math.floor(options.maxAgeSeconds)}`
      : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=${path}; SameSite=${sameSite}${secure}${maxAge}`;
}

export function deleteCookie(name: string, path = '/'): void {
  if (typeof document === 'undefined') {
    return;
  }
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=${path}; SameSite=Strict${secure}; Max-Age=0`;
}
